import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Calendar } from 'lucide-react';
import { Button } from './button';
import { cn, formatDateBR, localDateISO, parseCalendarDate } from '../../lib/utils';

const WEEK_DAYS = ['D', 'S', 'T', 'Q', 'Q', 'S', 'S'];

function startOfMonth(date) {
    return new Date(date.getFullYear(), date.getMonth(), 1);
}

function endOfMonth(date) {
    return new Date(date.getFullYear(), date.getMonth() + 1, 0);
}

function addDays(date, days) {
    return new Date(date.getFullYear(), date.getMonth(), date.getDate() + days);
}

function addMonths(date, months) {
    return new Date(date.getFullYear(), date.getMonth() + months, 1);
}

export const DEFAULT_PRESETS = [
    { label: 'Hoje', range: () => ({ from: new Date(), to: new Date() }) },
    { label: 'Ontem', range: () => ({ from: addDays(new Date(), -1), to: addDays(new Date(), -1) }) },
    { label: 'Últimos 7 dias', range: () => ({ from: addDays(new Date(), -6), to: new Date() }) },
    { label: 'Últimos 30 dias', range: () => ({ from: addDays(new Date(), -29), to: new Date() }) },
    { label: 'Este mês', range: () => ({ from: startOfMonth(new Date()), to: new Date() }) },
    {
        label: 'Mês passado',
        range: () => {
            const lastMonth = addMonths(new Date(), -1);
            return { from: startOfMonth(lastMonth), to: endOfMonth(lastMonth) };
        },
    },
    {
        label: 'Este ano',
        range: () => ({ from: new Date(new Date().getFullYear(), 0, 1), to: new Date() }),
    },
    {
        label: 'Ano passado',
        range: () => {
            const year = new Date().getFullYear() - 1;
            return { from: new Date(year, 0, 1), to: new Date(year, 11, 31) };
        },
    },
];

/** Células da grade do mês, com nulos nos dias anteriores ao primeiro. */
function monthCells(monthDate) {
    const first = startOfMonth(monthDate);
    const totalDays = endOfMonth(monthDate).getDate();
    const cells = new Array(first.getDay()).fill(null);
    for (let day = 1; day <= totalDays; day += 1) {
        cells.push(new Date(monthDate.getFullYear(), monthDate.getMonth(), day));
    }
    return cells;
}

function monthTitle(monthDate) {
    const label = monthDate.toLocaleDateString('pt-BR', { month: 'long', year: 'numeric' });
    return label.charAt(0).toUpperCase() + label.slice(1);
}

function sameDay(a, b) {
    return !!a && !!b && localDateISO(a) === localDateISO(b);
}

function triggerLabel(value, placeholder) {
    const from = parseCalendarDate(value?.from);
    const to = parseCalendarDate(value?.to);
    if (!from && !to) return placeholder;
    if (from && to) return `${formatDateBR(from)} - ${formatDateBR(to)}`;
    return formatDateBR(from || to);
}

function MonthGrid({ monthDate, draft, hovered, onPick, onHover }) {
    const { from, to } = draft;
    const rangeEnd = to || hovered;

    const inRange = (day) => {
        if (!from || !rangeEnd) return false;
        const start = from <= rangeEnd ? from : rangeEnd;
        const end = from <= rangeEnd ? rangeEnd : from;
        return day >= start && day <= end;
    };

    return (
        <div className="w-[248px]">
            <div className="h-8 flex items-center justify-center text-sm font-medium text-gray-700">
                {monthTitle(monthDate)}
            </div>
            <div className="grid grid-cols-7">
                {WEEK_DAYS.map((day, index) => (
                    <div key={`${day}-${index}`} className="h-8 flex items-center justify-center text-xs text-gray-500">
                        {day}
                    </div>
                ))}
                {monthCells(monthDate).map((day, index) => {
                    if (!day) {
                        return <div key={`empty-${index}`} className="h-8" />;
                    }
                    const isEdge = sameDay(day, from) || sameDay(day, to);
                    return (
                        <button
                            key={localDateISO(day)}
                            type="button"
                            onClick={() => onPick(day)}
                            onMouseEnter={() => onHover(day)}
                            className={cn(
                                'h-8 text-sm tabular-nums rounded-md transition-colors',
                                !isEdge && inRange(day) && 'bg-primary-50 text-primary-700 rounded-none',
                                isEdge ? 'bg-primary-600 text-white' : 'hover:bg-gray-100',
                            )}
                        >
                            {day.getDate()}
                        </button>
                    );
                })}
            </div>
        </div>
    );
}

/**
 * Seletor de período com atalhos e dois meses. `value` e `onChange` usam
 * datas ISO (YYYY-MM-DD); `to` nulo repete `from`.
 */
function DateRangePicker({
    value,
    onChange,
    placeholder = 'Selecione um período',
    disabled = false,
    className,
    align = 'start',
    presets = DEFAULT_PRESETS,
    'aria-label': ariaLabel = 'Período',
}) {
    const [open, setOpen] = useState(false);
    const [draft, setDraft] = useState({ from: null, to: null });
    const [hovered, setHovered] = useState(null);
    const [firstMonth, setFirstMonth] = useState(() => startOfMonth(new Date()));
    const containerRef = useRef(null);
    const popoverRef = useRef(null);
    const [position, setPosition] = useState({ top: 0, left: 0 });

    useEffect(() => {
        if (!open) return undefined;

        const handleClickOutside = (event) => {
            const inside = containerRef.current?.contains(event.target) || popoverRef.current?.contains(event.target);
            if (!inside) setOpen(false);
        };
        // Captura para fechar só o calendário, sem fechar o drawer que o contém
        const handleKeyDown = (event) => {
            if (event.key === 'Escape') {
                event.preventDefault();
                event.stopPropagation();
                setOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        document.addEventListener('keydown', handleKeyDown, true);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
            document.removeEventListener('keydown', handleKeyDown, true);
        };
    }, [open]);

    // Posiciona o calendário em relação à viewport para não ser cortado por containers com rolagem
    useLayoutEffect(() => {
        if (!open || !containerRef.current || !popoverRef.current) return undefined;
        const place = () => {
            const trigger = containerRef.current.getBoundingClientRect();
            const popover = popoverRef.current.getBoundingClientRect();
            const margin = 8;
            let left = align === 'end' ? trigger.right - popover.width : trigger.left;
            left = Math.max(margin, Math.min(left, window.innerWidth - popover.width - margin));
            let top = trigger.bottom + 4;
            if (top + popover.height > window.innerHeight - margin) {
                top = Math.max(margin, trigger.top - popover.height - 4);
            }
            setPosition({ top, left });
        };
        place();
        window.addEventListener('resize', place);
        window.addEventListener('scroll', place, true);
        return () => {
            window.removeEventListener('resize', place);
            window.removeEventListener('scroll', place, true);
        };
    }, [open, align, firstMonth]);

    const label = useMemo(() => triggerLabel(value, placeholder), [value, placeholder]);

    const handleToggle = () => {
        if (disabled) return;
        if (!open) {
            const from = parseCalendarDate(value?.from);
            const to = parseCalendarDate(value?.to);
            setDraft({ from, to });
            setHovered(null);
            setFirstMonth(startOfMonth(from || new Date()));
        }
        setOpen((prev) => !prev);
    };

    const handlePick = (day) => {
        setDraft((prev) => {
            if (!prev.from || prev.to || day < prev.from) {
                return { from: day, to: null };
            }
            return { ...prev, to: day };
        });
    };

    const handlePreset = (preset) => {
        const range = preset.range();
        setDraft(range);
        setHovered(null);
        setFirstMonth(startOfMonth(range.from));
    };

    const handleApply = () => {
        onChange({
            from: localDateISO(draft.from),
            to: localDateISO(draft.to || draft.from),
        });
        setOpen(false);
    };

    const draftLabel = draft.from
        ? `${formatDateBR(draft.from)} - ${formatDateBR(draft.to || draft.from)}`
        : '-';

    return (
        <div ref={containerRef} className={cn('relative shrink-0', className)}>
            <Button
                type="button"
                variant="outline"
                onClick={handleToggle}
                disabled={disabled}
                aria-label={ariaLabel}
                aria-expanded={open}
                className="w-full justify-start gap-2 font-normal tabular-nums"
            >
                <Calendar className="w-4 h-4 shrink-0 text-gray-500" />
                <span className="truncate">{label}</span>
            </Button>

            {open && createPortal(
                <div
                    ref={popoverRef}
                    style={{ top: position.top, left: position.left }}
                    className="fixed z-[200] flex bg-white border border-gray-300 rounded-lg shadow-lg"
                    onMouseLeave={() => setHovered(null)}
                >
                    <div className="hidden sm:flex w-40 flex-col gap-0.5 p-2 border-r border-gray-200">
                        {presets.map((preset) => (
                            <button
                                key={preset.label}
                                type="button"
                                onClick={() => handlePreset(preset)}
                                className="rounded-md px-2 py-1.5 text-left text-sm text-gray-700 hover:bg-gray-50"
                            >
                                {preset.label}
                            </button>
                        ))}
                    </div>
                    <div className="flex flex-col">
                        <div className="flex items-center justify-between px-3 pt-3">
                            <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                onClick={() => setFirstMonth((m) => addMonths(m, -1))}
                                aria-label="Mês anterior"
                            >
                                {'<'}
                            </Button>
                            <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                onClick={() => setFirstMonth((m) => addMonths(m, 1))}
                                aria-label="Próximo mês"
                            >
                                {'>'}
                            </Button>
                        </div>
                        <div className="flex gap-4 p-3">
                            <MonthGrid
                                monthDate={firstMonth}
                                draft={draft}
                                hovered={hovered}
                                onPick={handlePick}
                                onHover={setHovered}
                            />
                            <div className="hidden lg:block">
                                <MonthGrid
                                    monthDate={addMonths(firstMonth, 1)}
                                    draft={draft}
                                    hovered={hovered}
                                    onPick={handlePick}
                                    onHover={setHovered}
                                />
                            </div>
                        </div>
                        <div className="flex items-center justify-between gap-2 border-t border-gray-200 p-3">
                            <span className="text-sm text-gray-600 tabular-nums">{draftLabel}</span>
                            <div className="flex gap-2">
                                <Button type="button" variant="ghost" size="sm" onClick={() => setOpen(false)}>
                                    Cancelar
                                </Button>
                                <Button type="button" size="sm" onClick={handleApply} disabled={!draft.from}>
                                    Aplicar
                                </Button>
                            </div>
                        </div>
                    </div>
                </div>,
                document.body
            )}
        </div>
    );
}

export { DateRangePicker };
